import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { MatIcon } from '@angular/material/icon';

/** Type de message contextuel (propriété « Property 1 » de la maquette). */
export type MessageContextuelType = 'success' | 'error' | 'alert' | 'info';

/** Glyphe Material affiché par défaut dans la pastille, par type. */
const ICONES: Record<MessageContextuelType, string> = {
  success: 'check',
  error: 'highlight_off',
  alert: 'warning_amber',
  info: 'info_outline',
};

/** Ce glyphe occupe le bas de sa boîte : la maquette le remonte pour le centrer optiquement. */
const GLYPHE_A_REMONTER = 'warning_amber';

/**
 * Message contextuel : bandeau affiché dans une section de l'écran pour porter
 * une information importante.
 *
 * Il se décline en quatre types — succès (vert), erreur (rouge), alerte
 * (orange) et information (bleu). La pastille (`showIcon`, `icon`) comme la
 * croix de fermeture (`closable`) restent facultatives.
 *
 * Il remplace `lib-pegase-alert` dans les pages : ce dernier porte désormais le
 * visuel des alertes, qui passent par le composant snack.
 *
 * Le libellé se fournit soit par l'entrée `message`, soit par projection de
 * contenu quand il faut mettre certains mots en valeur :
 *
 * ```html
 * <lib-pegase-message-contextuel type="info">
 *   Descriptif avec certains <strong>mots mis en valeur</strong> et d'autres non.
 * </lib-pegase-message-contextuel>
 * ```
 */
@Component({
  selector: 'lib-pegase-message-contextuel',
  templateUrl: './message-contextuel.component.html',
  styleUrl: './message-contextuel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIcon],
  standalone: true,
})
export class MessageContextuelComponent {
  public type = input.required<MessageContextuelType>();

  public message = input<string>();

  /** La pastille est facultative : `false` laisse le libellé seul dans le bandeau. */
  public showIcon = input(true);

  /**
   * Glyphe Material à afficher à la place de celui du type, pour reprendre un
   * symbole déjà présent dans l'écran. La couleur de la pastille reste celle du
   * type.
   */
  public icon = input<string>();

  public closable = input(true);

  public closeLabel = input('Fermer le message');

  readonly closed = output<void>();

  private readonly ferme = signal(false);
  readonly visible = computed(() => !this.ferme());

  readonly glyphe = computed(() => this.icon() ?? ICONES[this.type()]);

  readonly glypheARemonter = computed(() => this.glyphe() === GLYPHE_A_REMONTER);

  /**
   * Erreur et alerte interrompent la tâche en cours : elles sont annoncées de
   * façon assertive, là où succès et information restent des mises à jour de
   * statut.
   */
  readonly role = computed(() => (this.type() === 'error' || this.type() === 'alert' ? 'alert' : 'status'));

  close(): void {
    this.ferme.set(true);
    this.closed.emit();
  }
}
